import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Linkedin, X } from 'lucide-react';
import { FadeUp } from './MaskReveal';
import { TEAM } from '../../lib/site-data';

function getInitials(name) {
  const parts = name.replace(/\./g, '').split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Team() {
  const [active, setActive] = useState(null);
  const member = active !== null ? TEAM[active] : null;

  return (
    <section id="team" data-testid="team-section" className="relative bg-ink-800 py-16 md:py-24">
      <div className="container-editorial">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <FadeUp>
              <span className="eyebrow">02 — Our Core Team</span>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="display-heading mt-6 text-white text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
                Relentless Minds.<br />
                <span className="text-white/40">World-Class Success.</span>
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <p className="text-white/60 text-base leading-relaxed max-w-sm">
              Practitioners who have led people, culture and search across four continents — and are still
              obsessed with getting the work right.
            </p>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/10">
          {TEAM.map((m, i) => (
            <motion.button
              key={m.name}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`team-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 }}
              className="group relative text-left bg-ink-900 aspect-[4/5] flex flex-col justify-between overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-signal-500"
            >
              {/* Portrait or monogram */}
              {m.photo ? (
                <>
                  <img
                    src={m.photo}
                    alt={m.name}
                    loading="lazy"
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover grayscale contrast-[1.05] brightness-90 group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-[1.04] transition-[filter,transform] duration-[900ms] ease-out"
                    style={{ objectPosition: 'center 20%' }}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10 group-hover:from-black/95 group-hover:via-black/25 group-hover:to-black/0 transition-colors duration-700" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent" />
                </>
              ) : (
                <>
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 flex items-center justify-center display-heading text-white/5 group-hover:text-signal-500/30 transition-colors duration-700"
                    style={{ fontSize: 'clamp(180px, 26vw, 320px)', letterSpacing: '-0.06em', lineHeight: 0.85 }}
                  >
                    {getInitials(m.name)}
                  </span>
                </>
              )}

              {/* Corner marks */}
              <span className="pointer-events-none absolute top-4 left-4 h-3 w-3 border-t border-l border-white/40 group-hover:border-signal-500 transition-colors duration-500" />
              <span className="pointer-events-none absolute top-4 right-4 h-3 w-3 border-t border-r border-white/40 group-hover:border-signal-500 transition-colors duration-500" />
              <span className="pointer-events-none absolute bottom-4 left-4 h-3 w-3 border-b border-l border-white/40 group-hover:border-signal-500 transition-colors duration-500" />
              <span className="pointer-events-none absolute bottom-4 right-4 h-3 w-3 border-b border-r border-white/40 group-hover:border-signal-500 transition-colors duration-500" />

              <div className="relative p-8 md:p-10 flex items-start justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/70">
                  0{i + 1} / 0{TEAM.length}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-signal-500 bg-black/40 backdrop-blur-sm px-2 py-1">
                  {m.tag}
                </span>
              </div>

              <div className="relative p-8 md:p-10">
                <h3 className="display-heading text-2xl md:text-[26px] text-white leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
                  {m.name}
                </h3>
                <div className="mt-3 flex items-start justify-between gap-4">
                  <p className="text-white/80 text-sm leading-snug">{m.role}</p>
                  <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-white/75 group-hover:text-signal-500 transition-colors duration-500">
                    View
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {member && (
          <motion.div
            key="profile-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex items-center justify-center px-4 md:px-8"
            onClick={() => setActive(null)}
            data-testid="team-profile-overlay"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-4xl max-h-[88vh] overflow-y-auto bg-ink-900 border border-white/10 shadow-[0_40px_120px_-20px_rgba(77,139,255,0.25)]"
              onClick={(e) => e.stopPropagation()}
              data-testid="team-profile-dialog"
            >
              <button
                onClick={() => setActive(null)}
                aria-label="Close profile"
                data-testid="team-profile-close"
                className="absolute top-5 right-5 z-10 inline-flex h-10 w-10 items-center justify-center border border-white/15 rounded-full text-white/75 hover:text-white hover:border-signal-500 hover:bg-white/5 transition-colors duration-300"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-12">
                {/* Left: portrait or monogram */}
                <div className="md:col-span-5 relative bg-ink-800 border-b md:border-b-0 md:border-r border-white/10 min-h-[280px] md:min-h-[520px] flex items-center justify-center overflow-hidden">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{ objectPosition: 'center 20%' }}
                    />
                  ) : (
                    <span
                      aria-hidden
                      className="display-heading text-signal-500/25"
                      style={{ fontSize: 'clamp(140px, 22vw, 220px)', letterSpacing: '-0.06em', lineHeight: 0.85 }}
                    >
                      {getInitials(member.name)}
                    </span>
                  )}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <span className="pointer-events-none absolute top-4 left-4 h-3 w-3 border-t border-l border-white/40" />
                  <span className="pointer-events-none absolute top-4 right-4 h-3 w-3 border-t border-r border-white/40" />
                  <span className="pointer-events-none absolute bottom-4 left-4 h-3 w-3 border-b border-l border-white/40" />
                  <span className="pointer-events-none absolute bottom-4 right-4 h-3 w-3 border-b border-r border-white/40" />
                  <span className="absolute top-4 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-signal-500 bg-black/40 backdrop-blur-sm px-2 py-1">
                    {member.tag}
                  </span>
                </div>

                {/* Right: details */}
                <div className="md:col-span-7 p-6 md:p-10 lg:p-12">
                  <span className="eyebrow">Core Team · Profile</span>
                  <h3 className="display-heading mt-4 text-white text-3xl md:text-4xl leading-tight" data-testid="team-profile-name">
                    {member.name}
                  </h3>
                  <p className="mt-2 text-white/65 text-sm md:text-base">{member.role}</p>

                  <p className="mt-6 text-white/80 text-[15px] md:text-base leading-relaxed max-w-2xl" data-testid="team-profile-bio">
                    {member.bio}
                  </p>

                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-signal-500">
                        Industries
                      </p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {member.industries.map((it) => (
                          <span
                            key={it}
                            className="inline-flex items-center px-2.5 py-1 text-[12px] text-white/85 border border-white/12 bg-white/[0.03]"
                          >
                            {it}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-signal-500">
                        Core Expertise
                      </p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {member.expertise.map((it) => (
                          <span
                            key={it}
                            className="inline-flex items-center px-2.5 py-1 text-[12px] text-white/85 border border-signal-500/25 bg-signal-500/[0.06]"
                          >
                            {it}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-9 pt-6 border-t border-white/10 flex flex-wrap items-center gap-3 justify-between">
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="team-profile-linkedin"
                      className="btn-pill btn-blue"
                    >
                      <Linkedin className="h-4 w-4" />
                      Connect on LinkedIn
                    </a>
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
                      George &amp; Milton · Advisors
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
